import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { showCombinedErrorToast } from "~/components/shared/error-toast";
import CustomFormWrapper from "~/components/shared/form-wrapper";
import { Button, Icon, Stepper } from "~/components/ui";

import { CreateJSASchema, TCreateJSA } from "./types";
import HeaderStep from "./steps/header";
import HazardsStep from "./steps/hazards";
import SummaryStep from "./steps/summary";

const JSAForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TCreateJSA>({
    resolver: zodResolver(CreateJSASchema),
    mode: "onSubmit",
    defaultValues: {
      reference: `JSA-${new Date().getTime()}`,
      title: "",
      workLocation: "",
      teamMembers: [],
      jobDescription: "",
      equipmentRequired: "",
      plannedStartDate: new Date(),
      plannedEndDate: new Date(),
      type: 0,
    },
  });

  const onSubmit = async (data: TCreateJSA) => {
    try {
      console.log("JSA Data:", data);
      toast.success("Job Safety Analysis submitted successfully");
      navigate("/daily-ops/jsa");
    } catch (err) {
      toast.error("Failed to submit JSA");
    }
  };

  const STEPS = useMemo(
    () => [
      {
        title: "Job Details",
        description: "General details",
        content: (
          <HeaderStep control={control} errors={errors} register={register} />
        ),
      },
      {
        title: "Hazards",
        description: "Identify steps and hazards",
        content: <HazardsStep control={control} errors={errors} />,
      },
      {
        title: "Summary",
        description: "Review and submit",
        content: <SummaryStep watch={watch} />,
      },
    ],
    [control, errors, register, watch],
  );

  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => (prev === STEPS.length - 1 ? prev : prev + 1));
  }, [STEPS.length]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => (prev <= 0 ? prev : prev - 1));
  }, []);

  const [isSubmiting, setIsSubmiting] = useState(false);

  useEffect(() => {
    if (isSubmiting && Object.values(errors).length > 0) {
      showCombinedErrorToast(errors);
      setIsSubmiting(false);
    }
    return () => {
      setIsSubmiting(false);
    };
  }, [isSubmiting, errors]);

  return (
    <div className="relative w-full">
      <form id="create-jsa" onSubmit={handleSubmit(onSubmit)}>
        <CustomFormWrapper className="">
          <div className="mx-auto w-full rounded-xl border border-neutral-200 bg-white px-10 py-8 mb-6">
            <Stepper
              currentStep={currentStep}
              steps={STEPS}
              setCurrentStep={setCurrentStep}
            />
          </div>
          {STEPS[currentStep].content}
        </CustomFormWrapper>

        <div className="flex justify-end gap-2 px-6 py-4 mt-4">
          <Button
            type="button"
            className="flex items-center gap-1.5 px-4 py-2 font-Medium text-sm"
            variant="outline"
            onClick={() => navigate("/daily-ops/jsa")}
          >
            Cancel
          </Button>

          <Button
            disabled={currentStep === 0}
            type="button"
            onClick={goToPreviousStep}
            variant="secondary"
            className="flex items-center gap-1.5 px-4 py-2 font-Medium text-sm"
          >
            <Icon name="ArrowLeft" className="h-5 w-5" />
            <span>Previous</span>
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={goToNextStep}
              variant="secondary"
              className="flex items-center gap-1.5 px-4 py-2 font-Medium text-sm"
            >
              <span>Next</span>
              <Icon name="ArrowRight" className="h-5 w-5" />
            </Button>
          ) : (
            <Button
              onClick={() => setIsSubmiting(true)}
              variant="default"
              className="flex items-center gap-1.5"
            >
              <span>Submit JSA</span>
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default JSAForm;
